import * as XLSX from "xlsx";

/**
 * Parses an Excel file following a dynamic pattern:
 * - Initial fields (before first group) go into "modelSpecifications"
 * - B column contains field names, C column contains values
 * - Cells in B that don't start with "_" indicate a new object group
 * - Cells in B that start with "_" are fields in the current group
 * - Empty B cell ends the current group
 * - "Door_Table" section: each row is a door (B=ID, C-L=door properties)
 * - "Window_Table" section: each row is a window (B=ID, C-O=window properties)
 * @param {File|string} fileOrPath - File object or path to the Excel file
 * @param {string} sheetName - Name of the sheet to read
 * @param {number} startRow - Starting row number (1-based, default: 2)
 * @returns {Promise<Object>} Object with grouped data, doors, and windows
 */
export const parseExcelFile = async (fileOrPath, sheetName, startRow = 2) => {
    try {
        let arrayBuffer;

        // Handle File object
        if (fileOrPath instanceof File) {
            arrayBuffer = await fileOrPath.arrayBuffer();
        } else {
            // Handle file path (for backward compatibility)
            const response = await fetch(fileOrPath);
            arrayBuffer = await response.arrayBuffer();
        }

        // Parse the workbook
        const workbook = XLSX.read(arrayBuffer, { type: "array" });

        // Get the specified sheet
        const sheet = workbook.Sheets[sheetName];

        if (!sheet) {
            throw new Error(`Sheet "${sheetName}" not found in the Excel file`);
        }

        // Column indices
        const colBIndex = 1; // Field names / IDs
        const colCIndex = 2; // Values start here

        // Convert startRow to 0-based
        let currentRow = startRow - 1;

        const result = {
            groups: {},
            doors: [],
            windows: [],
        };

        // Find the maximum row in the sheet
        const range = XLSX.utils.decode_range(sheet["!ref"] || "A1");
        const maxRow = range.e.r;

        let currentGroupName = null;
        let currentGroup = {};
        let foundFirstGroup = false;

        // Helper function to get cell value
        const getCellValue = (row, col) => {
            const cell = XLSX.utils.encode_cell({ r: row, c: col });
            return sheet[cell]?.v;
        };

        // Phase 1: Parse initial fields and groups until Door_Table
        while (currentRow <= maxRow) {
            const bValue = getCellValue(currentRow, colBIndex);
            const cValue = getCellValue(currentRow, colCIndex);

            // Check if we've reached "Door_Table"
            if (bValue === "Door_Table") {
                currentRow++; // Move past the "Door_Table" row
                break;
            }

            // Skip if B cell is empty
            if (bValue === undefined || bValue === null || bValue === "") {
                // If we have a current group, end it
                if (currentGroupName !== null) {
                    result.groups[currentGroupName] = { ...currentGroup };
                    currentGroup = {};
                    currentGroupName = null;
                }
                currentRow++;
                continue;
            }

            // Convert B value to string for checking
            const bValueStr = String(bValue);

            // Check if this is a new group (doesn't start with "_")
            if (!bValueStr.startsWith("_")) {
                // If we have a previous group, save it first
                if (currentGroupName !== null) {
                    result.groups[currentGroupName] = { ...currentGroup };
                    currentGroup = {};
                }

                // Mark that we've found the first group
                if (!foundFirstGroup) {
                    foundFirstGroup = true;
                    // Save any initial fields to modelSpecifications
                    if (Object.keys(result.groups["modelSpecifications"] || {}).length > 0) {
                        // Already have modelSpecifications, continue
                    }
                }

                // Start new group
                currentGroupName = bValueStr;
            } else {
                // This is a field (starts with "_")
                if (!foundFirstGroup) {
                    // Before first group, add to modelSpecifications
                    if (!result.groups["modelSpecifications"]) {
                        result.groups["modelSpecifications"] = {};
                    }
                    const fieldName = bValueStr.substring(1);
                    result.groups["modelSpecifications"][fieldName] = cValue !== undefined ? cValue : null;
                } else {
                    // After first group, add to current group
                    if (currentGroupName === null) {
                        console.warn(`Found field "${bValueStr}" without a group at row ${currentRow + 1}`);
                    } else {
                        const fieldName = bValueStr.substring(1);
                        currentGroup[fieldName] = cValue !== undefined ? cValue : null;
                    }
                }
            }

            currentRow++;
        }

        // Save the last group if it exists
        if (currentGroupName !== null && Object.keys(currentGroup).length > 0) {
            result.groups[currentGroupName] = { ...currentGroup };
        }

        // Phase 2: Parse Door_Table section
        // Door headers: _Present, _Level, _Label, _Width (in), _Height (in), _Type, _ParentLabel, _ParentObject, _ParentObjectWorksheetAddress, _Direction (Cardinal)
        // Columns: B=ID, C=_Present, D=_Level, E=_Label, F=_Width (in), G=_Height (in), H=_Type, I=_ParentLabel, J=_ParentObject, K=_ParentObjectWorksheetAddress, L=_Direction (Cardinal)
        const doorHeaders = [
            "_Present",
            "_Level",
            "_Label",
            "_Width (in)",
            "_Height (in)",
            "_Type",
            "_ParentLabel",
            "_ParentObject",
            "_ParentObjectWorksheetAddress",
            "_Direction (Cardinal)",
        ];

        while (currentRow <= maxRow) {
            const bValue = getCellValue(currentRow, colBIndex);

            // Empty B cell ends door section
            if (bValue === undefined || bValue === null || bValue === "") {
                currentRow++;
                break;
            }

            // Check for Window_Table
            if (bValue === "Window_Table") {
                currentRow++; // Move past the "Window_Table" row
                break;
            }

            // Parse door row
            const doorId = String(bValue);
            const presentValue = getCellValue(currentRow, colCIndex);

            // Only process if _Present is true
            if (presentValue === true || presentValue === "true" || presentValue === "TRUE" || presentValue === 1) {
                const door = {
                    id: doorId,
                };

                // Parse door properties (columns C through K, indices 2-10)
                for (let i = 0; i < doorHeaders.length; i++) {
                    const colIndex = colCIndex + i;
                    const value = getCellValue(currentRow, colIndex);
                    const headerName = doorHeaders[i].substring(1); // Remove leading "_"
                    door[headerName] = value !== undefined ? value : null;
                }

                result.doors.push(door);
            }

            currentRow++;
        }

        // Phase 3: Parse Window_Table section
        // Window headers: _Present, _Level, _Label, _Direction (Cardinal), _Width (in), _Height (in), _Overhang Width (ft), _Overhang Height (ft), _Count, _Type, _ParentLabel, _ParentObject, _ParentObjectWorksheetAddress
        // Columns: B=ID, C=_Present, D=_Level, E=_Label, F=_Direction (Cardinal), G=_Width (in), H=_Height (in), I=_Overhang Width (ft), J=_Overhang Height (ft), K=_Count, L=_Type, M=_ParentLabel, N=_ParentObject, O=_ParentObjectWorksheetAddress
        const windowHeaders = [
            "_Present",
            "_Level",
            "_Label",
            "_Direction (Cardinal)",
            "_Width (in)",
            "_Height (in)",
            "_Overhang Width (ft)",
            "_Overhang Height (ft)",
            "_Count",
            "_Type",
            "_ParentLabel",
            "_ParentObject",
            "_ParentObjectWorksheetAddress",
        ];

        while (currentRow <= maxRow) {
            const bValue = getCellValue(currentRow, colBIndex);

            // Empty B cell ends window section
            if (bValue === undefined || bValue === null || bValue === "") {
                break;
            }

            // Parse window row
            const windowId = String(bValue);
            const presentValue = getCellValue(currentRow, colCIndex);

            // Only process if _Present is true
            if (presentValue === true || presentValue === "true" || presentValue === "TRUE" || presentValue === 1) {
                const window = {
                    id: windowId,
                };

                // Parse window properties (columns C through O, indices 2-14)
                for (let i = 0; i < windowHeaders.length; i++) {
                    const colIndex = colCIndex + i;
                    const value = getCellValue(currentRow, colIndex);
                    const headerName = windowHeaders[i].substring(1); // Remove leading "_"
                    window[headerName] = value !== undefined ? value : null;
                }

                result.windows.push(window);
            }

            currentRow++;
        }

        return result;
    } catch (error) {
        console.error("Error parsing Excel file:", error);
        throw error;
    }
};
